/**
 * Phase 2 · Menu management: items with pricing & availability, categories,
 * discounts and tax rates. Management actions are permission-gated
 * (menu:manage) and hidden from read-only roles.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { http, ApiError } from '../api/client';
import { useAuth } from '../store/auth';
import { useUi } from '../store/ui';
import { money, pct } from '../lib/format';
import { DataTable, type Column } from '../components/ui/DataTable';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { Input, Select, Textarea } from '../components/ui/Input';

interface MenuItem {
  id: string;
  name: string;
  description: string | null;
  base_price: number;
  happy_price: number | null;
  is_available: boolean;
  is_combo: boolean;
  category_name: string | null;
  category_id: string | null;
  tax_rate_id: string | null;
  food_cost_pct: number | null;
  recipe_id: string | null;
  sort_order: number;
}
interface Category { id: string; name: string; item_count: number; sort_order: number; is_active: boolean }
interface Discount { id: string; name: string; kind: string; value: number; is_active: boolean }
interface TaxRate { id: string; name: string; rate_pct: number; is_inclusive: boolean }

const TABS = ['Items', 'Categories', 'Pricing'] as const;

export function MenuPage() {
  const can = useAuth((s) => s.user?.permissions.includes('menu:manage') ?? false);
  const toast = useUi((s) => s.toast);
  const [tab, setTab] = useState<(typeof TABS)[number]>('Items');
  const [items, setItems] = useState<MenuItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [taxes, setTaxes] = useState<TaxRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [itemModal, setItemModal] = useState<null | Partial<MenuItem>>(null);
  const [catModal, setCatModal] = useState<null | Partial<Category>>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, c, d, t] = await Promise.all([
        http.get<{ items: MenuItem[] }>('/menu/items'),
        http.get<{ categories: Category[] }>('/menu/categories'),
        http.get<{ discounts: Discount[] }>('/menu/discounts'),
        http.get<{ tax_rates: TaxRate[] }>('/menu/tax-rates'),
      ]);
      setItems(m.items); setCategories(c.categories); setDiscounts(d.discounts); setTaxes(t.tax_rates);
    } catch (e) {
      toast({ kind: 'error', title: 'Load failed', message: e instanceof ApiError ? e.message : String(e) });
    } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return s ? items.filter((i) => i.name.toLowerCase().includes(s) || (i.category_name ?? '').toLowerCase().includes(s)) : items;
  }, [items, search]);

  const toggleAvailability = async (item: MenuItem) => {
    try {
      await http.post(`/menu/items/${item.id}/availability`, { available: !item.is_available });
      toast({ kind: 'success', title: item.is_available ? 'Marked sold out' : 'Back on sale', message: item.name });
      await load();
    } catch (e) {
      toast({ kind: 'error', title: 'Failed', message: e instanceof ApiError ? e.message : String(e) });
    }
  };

  const itemCols: Column<MenuItem>[] = [
    { key: 'name', header: 'Item', sortable: true, render: (r) => (
      <div>
        <p className="font-medium text-ink">{r.name}</p>
        <p className="text-xs text-ink-muted">{r.category_name ?? 'Uncategorised'}</p>
      </div>
    ) },
    { key: 'base_price', header: 'Price', render: (r) => (
      <div>
        <p>{money(r.base_price)}</p>
        {r.happy_price !== null && <p className="text-xs text-brand-500">HH {money(r.happy_price)}</p>}
      </div>
    ) },
    { key: 'food_cost_pct', header: 'Food cost', render: (r) => r.recipe_id ? pct(r.food_cost_pct) : <span className="text-ink-muted">no recipe</span> },
    { key: 'is_available', header: 'Status', render: (r) => r.is_available ? <Badge tone="success">On sale</Badge> : <Badge tone="danger">Sold out</Badge> },
    ...(can ? [{
      key: 'actions', header: '', render: (r: MenuItem) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={() => void toggleAvailability(r)}>{r.is_available ? '86 it' : 'Restore'}</Button>
          <Button size="sm" variant="secondary" onClick={() => setItemModal(r)}>Edit</Button>
        </div>
      ),
    }] as Column<MenuItem>[] : []),
  ];

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">Menu</h1>
          <p className="text-sm text-ink-muted">{items.length} items · {categories.length} categories</p>
        </div>
        {can && <Button onClick={() => setItemModal({})}><Plus size={16} /> New item</Button>}
      </header>

      <div className="flex gap-1 rounded-lg bg-surface-1 p-1" role="tablist">
        {TABS.map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${tab === t ? 'bg-surface-0 text-ink shadow-sm' : 'text-ink-muted hover:text-ink'}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'Items' && (
        <>
          <div className="relative max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
            <Input aria-label="Search menu" className="pl-9" placeholder="Search items…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <DataTable columns={itemCols} rows={filtered} loading={loading}
            onRowClick={can ? (r) => setItemModal(r) : undefined} />
        </>
      )}

      {tab === 'Categories' && (
        <>
          {can && <div className="flex justify-end"><Button onClick={() => setCatModal({})}><Plus size={16} /> New category</Button></div>}
          <DataTable columns={[
            { key: 'name', header: 'Category', sortable: true },
            { key: 'item_count', header: 'Items' },
            { key: 'sort_order', header: 'Sort' },
            { key: 'is_active', header: 'Status', render: (r: Category) => r.is_active ? <Badge tone="success">Active</Badge> : <Badge>Hidden</Badge> },
            ...(can ? [{ key: 'edit', header: '', render: (r: Category) => <div className="flex justify-end"><Button size="sm" variant="secondary" onClick={() => setCatModal(r)}>Edit</Button></div> }] as Column<Category>[] : []),
          ]} rows={categories} loading={loading} />
        </>
      )}

      {tab === 'Pricing' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <section className="rounded-xl border border-line bg-surface-0 p-4">
            <h2 className="mb-3 text-sm font-semibold text-ink">Discounts</h2>
            <DataTable columns={[
              { key: 'name', header: 'Name' },
              { key: 'value', header: 'Value', render: (r: Discount) => r.kind === 'percent' ? pct(r.value) : money(r.value) },
              { key: 'is_active', header: 'Status', render: (r: Discount) => r.is_active ? <Badge tone="success">Active</Badge> : <Badge>Off</Badge> },
            ]} rows={discounts} loading={loading} />
          </section>
          <section className="rounded-xl border border-line bg-surface-0 p-4">
            <h2 className="mb-3 text-sm font-semibold text-ink">Tax rates</h2>
            <DataTable columns={[
              { key: 'name', header: 'Name' },
              { key: 'rate_pct', header: 'Rate', render: (r: TaxRate) => pct(r.rate_pct) },
              { key: 'is_inclusive', header: 'Application', render: (r: TaxRate) => r.is_inclusive ? 'Inclusive' : 'Exclusive' },
            ]} rows={taxes} loading={loading} />
          </section>
        </div>
      )}

      {itemModal && (
        <ItemModal initial={itemModal} categories={categories} taxes={taxes} onClose={() => setItemModal(null)}
          onSaved={async () => { setItemModal(null); await load(); }} />
      )}
      {catModal && (
        <CategoryModal initial={catModal} onClose={() => setCatModal(null)} onSaved={async () => { setCatModal(null); await load(); }} />
      )}
    </div>
  );
}

function ItemModal({ initial, categories, taxes, onClose, onSaved }: {
  initial: Partial<MenuItem>; categories: Category[]; taxes: TaxRate[];
  onClose: () => void; onSaved: () => Promise<void>;
}) {
  const toast = useUi((s) => s.toast);
  const editing = Boolean(initial.id);
  const [name, setName] = useState(initial.name ?? '');
  const [categoryId, setCategoryId] = useState(initial.category_id ?? (categories[0]?.id ?? ''));
  const [price, setPrice] = useState(initial.base_price ? (Number(initial.base_price) / 100).toFixed(2) : '');
  const [happy, setHappy] = useState(initial.happy_price != null ? (Number(initial.happy_price) / 100).toFixed(2) : '');
  const [description, setDescription] = useState(initial.description ?? '');
  const [taxRateId, setTaxRateId] = useState(initial.tax_rate_id ?? '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const body = {
      name, categoryId: categoryId || null,
      basePrice: Math.round(parseFloat(price || '0') * 100),
      happyPrice: happy ? Math.round(parseFloat(happy) * 100) : null,
      description: description || undefined,
      taxRateId: taxRateId || null,
    };
    setBusy(true);
    try {
      if (editing) await http.patch(`/menu/items/${initial.id}`, body);
      else await http.post('/menu/items', body);
      toast({ kind: 'success', title: editing ? 'Item updated' : 'Item created', message: name });
      await onSaved();
    } catch (e) {
      toast({ kind: 'error', title: 'Save failed', message: e instanceof ApiError ? e.message : String(e) });
    } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={editing ? 'Edit item' : 'New menu item'}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={busy} onClick={() => void save()}>Save</Button></>}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
        <Select label="Category" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
        <Input label="Price (£)" type="number" step="0.01" min="0" value={price} onChange={(e) => setPrice(e.target.value)} />
        <Input label="Happy-hour price (£)" type="number" step="0.01" min="0" value={happy} onChange={(e) => setHappy(e.target.value)} />
        <Select label="Tax rate" value={taxRateId} onChange={(e) => setTaxRateId(e.target.value)}>
          <option value="">None</option>
          {taxes.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.rate_pct}%)</option>)}
        </Select>
        <div className="sm:col-span-2"><Textarea label="Description" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} /></div>
      </div>
    </Modal>
  );
}

function CategoryModal({ initial, onClose, onSaved }: {
  initial: Partial<Category>; onClose: () => void; onSaved: () => Promise<void>;
}) {
  const toast = useUi((s) => s.toast);
  const editing = Boolean(initial.id);
  const [name, setName] = useState(initial.name ?? '');
  const [sortOrder, setSortOrder] = useState(String(initial.sort_order ?? 0));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const body = { name, sortOrder: parseInt(sortOrder || '0', 10) };
      if (editing) await http.patch(`/menu/categories/${initial.id}`, body);
      else await http.post('/menu/categories', body);
      toast({ kind: 'success', title: editing ? 'Category updated' : 'Category created', message: name });
      await onSaved();
    } catch (e) {
      toast({ kind: 'error', title: 'Save failed', message: e instanceof ApiError ? e.message : String(e) });
    } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={editing ? 'Edit category' : 'New category'}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={busy} onClick={() => void save()}>Save</Button></>}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
        <Input label="Sort order" type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
      </div>
    </Modal>
  );
}
